import listenerMiddleware from "App/listenerMiddleware";
import documentsActionTypes from "Areas/Documents/redux/actionTypes";
import { actionTypes as signalRActionTypes } from "Areas/SharedFeatures/signalRMiddleware";
import documentsSidebarMenuActionTypes from "Areas/Sidebar/Documents/redux/actionTypes";
import { getActiveDocumentId } from "Areas/Sidebar/Documents/redux/getActiveDocumentId";
import createReducer from "Common/reducerUtils/createReducer";
import set from "Common/reducerUtils/set";
import difference from "lodash/difference";
import forEach from "lodash/forEach";
import includes from "lodash/includes";
import keyBy from "lodash/keyBy";
import map from "lodash/map";
import omit from "lodash/omit";
import uniq from "lodash/uniq";
import { v4 as uuid } from "uuid";
import { NAMESPACE } from "../constants";
import actionCreators from "./actionCreators";
import actionTypes from "./actionTypes";
import reduxStoreExternal from "./reduxStoreExternal";
export const initialState = {
    [NAMESPACE]: {
        graphInstanceKey: {},
        sourceDataKey: {},
        nodesSourceKey: {},
        edgesSourceKey: {},
        updatesKey: {},
        nodesSourceDocumentObjectId: {},
        saveTriggered: {},
        showedResultsSourceTaskIds: {},
        sourceTaskIdsToHide: {},
        sourceTaskIdsToShow: {},
        detailedPanelOpen: undefined,
        statisticData: {},
        layoutAlgorithm: {},
        hasChanges: {},
        tableModalState: false,
        groupTableData: {}
    }
};
const setUpdatesForDocument = (state, payload, documentId, appendToPrevData) => {
    var _a;
    const time = new Date().getTime();
    if (appendToPrevData && payload && reduxStoreExternal.updates[documentId]) {
        reduxStoreExternal.updates[documentId] = {
            NodesSource: {
                ...reduxStoreExternal.updates[documentId].NodesSource,
                ...keyBy(payload.NodesSource, (e) => `${time}_${e.NodeId}`)
            },
            EdgesSource: {
                ...reduxStoreExternal.updates[documentId].EdgesSource,
                ...keyBy(payload.EdgesSource, (e) => `${time}_${e.EdgeId}`)
            },
            Groups: ((_a = payload.Groups) === null || _a === void 0 ? void 0 : _a.length)
                ? {
                    ...reduxStoreExternal.updates[documentId].Groups,
                    ...keyBy(payload.Groups, "Id")
                }
                : reduxStoreExternal.updates[documentId].Groups
        };
    }
    else {
        reduxStoreExternal.updates[documentId] = {
            NodesSource: keyBy(payload === null || payload === void 0 ? void 0 : payload.NodesSource, (e) => `${time}_${e.NodeId}`),
            EdgesSource: keyBy(payload === null || payload === void 0 ? void 0 : payload.EdgesSource, (e) => `${time}_${e.EdgeId}`),
            Groups: keyBy(payload === null || payload === void 0 ? void 0 : payload.Groups, "Id")
        };
    }
    return set(state, `[${NAMESPACE}].updatesKey[${documentId}]`, uuid());
};
const setGraphDataForDocument = (state, payload, documentId, updateGraphInstanceKey) => {
    let result = state;
    if (updateGraphInstanceKey) {
        reduxStoreExternal.attributeImage = omit(reduxStoreExternal.attributeImage, documentId);
        reduxStoreExternal.updates = omit(reduxStoreExternal.updates, documentId);
        reduxStoreExternal.groupsLoaded = omit(reduxStoreExternal.groupsLoaded, documentId);
        result = set(result, `[${NAMESPACE}].sourceTaskIdsToShow[${documentId}]`, undefined);
        result = set(result, `[${NAMESPACE}].sourceTaskIdsToHide[${documentId}]`, undefined);
        result = set(result, `[${NAMESPACE}].updatesKey[${documentId}]`, undefined);
        result = set(result, `[${NAMESPACE}].graphInstanceKey[${documentId}]`, uuid());
    }
    const documentEdgesSourceMap = keyBy(payload.EdgesSource, "EdgeId");
    const documentGroupsMap = keyBy(payload.Groups, "Id");
    const documentNodesSourceMap = {};
    const nodesSourceDocumentObjectId = new Set();
    const showedResultsSourceTaskIds = new Set();
    forEach(payload.NodesSource, e => {
        documentNodesSourceMap[e.NodeId] = e;
        nodesSourceDocumentObjectId.add(e.DocumentObjectId);
        e.SourceTaskId && showedResultsSourceTaskIds.add(e.SourceTaskId);
    });
    reduxStoreExternal.sourceData[documentId] = {
        Id: payload.Id,
        GraphModel: payload.GraphModel || "",
        LayoutAlgorithm: payload.LayoutAlgorithm,
        NodesSource: documentNodesSourceMap,
        EdgesSource: documentEdgesSourceMap,
        Groups: documentGroupsMap,
        DataGroups: payload.DataGroups,
        Nodes: payload.Nodes,
        Edges: payload.Edges
    };
    result = set(result, `[${NAMESPACE}].sourceDataKey[${documentId}]`, uuid());
    reduxStoreExternal.nodesSource[documentId] = documentNodesSourceMap;
    result = set(result, `[${NAMESPACE}].nodesSourceKey[${documentId}]`, uuid());
    const statisticsMap = {};
    const groupsLoaded = reduxStoreExternal.groupsLoaded[documentId];
    const groups = reduxStoreExternal.sourceData[documentId].Groups;
    const groupCompleted = new Set();
    forEach(documentNodesSourceMap, (e) => {
        if (!e.Created)
            return;
        if (!statisticsMap[e.Created])
            statisticsMap[e.Created] = 0;
        if (!e.GroupId || !e.PinnedToGroup || includes(groupsLoaded, e.GroupId)) {
            statisticsMap[e.Created] += 1;
        }
        else if (groups[e.GroupId] && !groupCompleted.has(e.GroupId)) {
            statisticsMap[e.Created] += groups[e.GroupId].ObjectCount;
            groupCompleted.add(e.GroupId);
        }
    });
    result = set(result, `[${NAMESPACE}].statisticData[${documentId}]`, map(statisticsMap, (count, created) => ({ Timestamp: Number.parseInt(created), Count: count })));
    reduxStoreExternal.edgesSource[documentId] = documentEdgesSourceMap;
    result = set(result, `[${NAMESPACE}].edgesSourceKey[${documentId}]`, uuid());
    result = set(result, `[${NAMESPACE}].nodesSourceDocumentObjectId[${documentId}]`, Array.from(nodesSourceDocumentObjectId));
    result = set(result, `[${NAMESPACE}].saveTriggered[${documentId}]`, false);
    result = set(result, `[${NAMESPACE}].showedResultsSourceTaskIds[${documentId}]`, Array.from(showedResultsSourceTaskIds));
    result = set(result, `[${NAMESPACE}].sourceTaskIdsToShow[${documentId}]`, undefined);
    result = set(result, `[${NAMESPACE}].sourceTaskIdsToHide[${documentId}]`, undefined);
    result = set(result, `[${NAMESPACE}].hasChanges[${documentId}]`, false);
    result = set(result, `[${NAMESPACE}].layoutAlgorithm[${documentId}]`, payload.LayoutAlgorithm);
    return result;
};
export default createReducer(initialState, {
    [actionTypes.GET_DOCUMENT_GRAPH_DATA_COMPLETED]: (state, { payload, meta }) => setGraphDataForDocument(state, payload, meta.documentId, true),
    [actionTypes.SAVE_DOCUMENT_GRAPH_DATA_COMPLETED]: (state, { meta }) => setGraphDataForDocument(state, meta.sourceData, meta.documentId, false),
    [actionTypes.GET_DOCUMENT_GRAPH_DATA_UPDATES_COMPLETED]: (state, { payload, meta }) => setUpdatesForDocument(state, payload, meta.documentId, true),
    [actionTypes.GET_SOURCE_TASKS_RESULTS_COMPLETED]: (state, { payload, meta }) => setUpdatesForDocument(state, payload, meta.documentId, true),
    [actionTypes.CLEAR_DATA]: (state, { documentId }) => {
        reduxStoreExternal.sourceData = omit(reduxStoreExternal.sourceData, documentId);
        reduxStoreExternal.nodesSource = omit(reduxStoreExternal.nodesSource, documentId);
        reduxStoreExternal.edgesSource = omit(reduxStoreExternal.edgesSource, documentId);
        reduxStoreExternal.attributeImage = omit(reduxStoreExternal.attributeImage, documentId);
        reduxStoreExternal.updates = omit(reduxStoreExternal.updates, documentId);
        reduxStoreExternal.groupsLoaded = omit(reduxStoreExternal.groupsLoaded, documentId);
        return {
            ...state,
            [NAMESPACE]: {
                ...state[NAMESPACE],
                sourceDataKey: omit(state[NAMESPACE].sourceDataKey, documentId),
                nodesSourceKey: omit(state[NAMESPACE].nodesSourceKey, documentId),
                edgesSourceKey: omit(state[NAMESPACE].edgesSourceKey, documentId),
                nodesSourceDocumentObjectId: omit(state[NAMESPACE].nodesSourceDocumentObjectId, documentId),
                updatesKey: omit(state[NAMESPACE].updatesKey, documentId),
                saveTriggered: omit(state[NAMESPACE].saveTriggered, documentId),
                showedResultsSourceTaskIds: omit(state[NAMESPACE].showedResultsSourceTaskIds, documentId),
                sourceTaskIdsToShow: omit(state[NAMESPACE].sourceTaskIdsToShow, documentId),
                sourceTaskIdsToHide: omit(state[NAMESPACE].sourceTaskIdsToHide, documentId),
                statisticData: omit(state[NAMESPACE].statisticData, documentId),
                layoutAlgorithm: omit(state[NAMESPACE].layoutAlgorithm, documentId),
                hasChanges: omit(state[NAMESPACE].hasChanges, documentId),
                groupTableData: omit(state[NAMESPACE].groupTableData, documentId)
            }
        };
    },
    [actionTypes.SET_GRAPH_MODEL]: (state, { documentId, data, updateDraftData, updateSourceData, updateNodesSourceDocumentObjectId }) => {
        if (!reduxStoreExternal.sourceData[documentId])
            return state;
        let result = state;
        if (data.model !== undefined && updateSourceData) {
            reduxStoreExternal.sourceData[documentId].GraphModel = data.model;
        }
        if (data.groups !== undefined && updateSourceData) {
            reduxStoreExternal.sourceData[documentId].Groups = {
                ...(reduxStoreExternal.sourceData[documentId].Groups || {}),
                ...keyBy(data.groups, "Id")
            };
        }
        if (data.nodesSource !== undefined) {
            const documentNodesSourceMap = {};
            const nodesSourceDocumentObjectId = new Set();
            const showedResultsSourceTaskIds = new Set();
            const callback = (e) => {
                documentNodesSourceMap[e.NodeId] = e;
                nodesSourceDocumentObjectId.add(e.DocumentObjectId);
                if (e.SourceTaskId)
                    showedResultsSourceTaskIds.add(e.SourceTaskId);
            };
            if (data.onlyDiffSources) {
                forEach(reduxStoreExternal.nodesSource[documentId], callback);
            }
            forEach(data.nodesSource, callback);
            if (updateNodesSourceDocumentObjectId) {
                const statisticsMap = {};
                const groupsLoaded = reduxStoreExternal.groupsLoaded[documentId];
                const groups = reduxStoreExternal.sourceData[documentId].Groups;
                const groupCompleted = new Set();
                forEach(documentNodesSourceMap, (e) => {
                    if (!e.Created)
                        return;
                    if (!statisticsMap[e.Created])
                        statisticsMap[e.Created] = 0;
                    if (!e.GroupId || !e.PinnedToGroup || includes(groupsLoaded, e.GroupId)) {
                        statisticsMap[e.Created] += 1;
                    }
                    else if (groups[e.GroupId] && !groupCompleted.has(e.GroupId)) {
                        statisticsMap[e.Created] += groups[e.GroupId].ObjectCount;
                        groupCompleted.add(e.GroupId);
                    }
                });
                result = set(result, `[${NAMESPACE}].statisticData[${documentId}]`, map(statisticsMap, (count, created) => ({
                    Timestamp: Number.parseInt(created),
                    Count: count
                })));
            }
            if (updateDraftData) {
                reduxStoreExternal.nodesSource[documentId] = documentNodesSourceMap;
                result = set(result, `[${NAMESPACE}].nodesSourceKey[${documentId}]`, uuid());
                result = set(result, `[${NAMESPACE}].showedResultsSourceTaskIds[${documentId}]`, Array.from(showedResultsSourceTaskIds));
            }
            if (updateNodesSourceDocumentObjectId) {
                result = set(result, `[${NAMESPACE}].nodesSourceDocumentObjectId[${documentId}]`, Array.from(nodesSourceDocumentObjectId));
            }
            if (updateSourceData) {
                reduxStoreExternal.sourceData[documentId].NodesSource = documentNodesSourceMap;
            }
        }
        if (data.edgesSource !== undefined) {
            const documentEdgesSourceMap = data.onlyDiffSources
                ? {
                    ...reduxStoreExternal.edgesSource[documentId],
                    ...keyBy(data.edgesSource, "EdgeId")
                }
                : keyBy(data.edgesSource, "EdgeId");
            if (updateDraftData) {
                reduxStoreExternal.edgesSource[documentId] = documentEdgesSourceMap;
                result = set(result, `[${NAMESPACE}].edgesSourceKey[${documentId}]`, uuid());
            }
            if (updateSourceData) {
                reduxStoreExternal.sourceData[documentId].EdgesSource = documentEdgesSourceMap;
            }
        }
        if (updateSourceData) {
            result = set(result, `[${NAMESPACE}].sourceDataKey[${documentId}]`, uuid());
        }
        return set(result, `[${NAMESPACE}].saveTriggered[${documentId}]`, false);
    },
    [actionTypes.SET_DETAILED_PANEL_OPEN]: (state, { detailedPanelOpen }) => set(state, `[${NAMESPACE}].detailedPanelOpen`, detailedPanelOpen),
    [actionTypes.SET_SAVE_TRIGGERED]: (state, { documentId, saveTriggered }) => set(state, `[${NAMESPACE}].saveTriggered[${documentId}]`, saveTriggered),
    [actionTypes.CLEAR_FROM_UPDATES]: (state, { documentId, nodesSourceUpdateRecordIds, edgesSourceUpdateRecordIds }) => {
        if (!reduxStoreExternal.updates[documentId])
            return state;
        if (nodesSourceUpdateRecordIds === null || nodesSourceUpdateRecordIds === void 0 ? void 0 : nodesSourceUpdateRecordIds.length)
            reduxStoreExternal.updates[documentId].NodesSource = omit(reduxStoreExternal.updates[documentId].NodesSource, nodesSourceUpdateRecordIds);
        if (edgesSourceUpdateRecordIds === null || edgesSourceUpdateRecordIds === void 0 ? void 0 : edgesSourceUpdateRecordIds.length)
            reduxStoreExternal.updates[documentId].EdgesSource = omit(reduxStoreExternal.updates[documentId].EdgesSource, edgesSourceUpdateRecordIds);
        return set(state, `[${NAMESPACE}].updatesKey[${documentId}]`, uuid());
    },
    [actionTypes.TOGGLE_SHOW_HIDE_TASK_RESULTS]: (state, { documentId, sourceTaskIds, show }) => {
        const prevToShow = state[NAMESPACE].sourceTaskIdsToShow[documentId] || [];
        const prevToHide = state[NAMESPACE].sourceTaskIdsToHide[documentId] || [];
        const result = set(state, `[${NAMESPACE}].sourceTaskIdsToShow[${documentId}]`, show ? uniq([...prevToShow, ...sourceTaskIds]) : difference(prevToShow, sourceTaskIds));
        return set(result, `[${NAMESPACE}].sourceTaskIdsToHide[${documentId}]`, !show ? uniq([...prevToHide, ...sourceTaskIds]) : difference(prevToHide, sourceTaskIds));
    },
    [actionTypes.CLEAR_FROM_SHOW_HIDE_TASK_RESULTS]: (state, { documentId, sourceTaskIds }) => {
        const prevToShow = state[NAMESPACE].sourceTaskIdsToShow[documentId] || [];
        const prevToHide = state[NAMESPACE].sourceTaskIdsToHide[documentId] || [];
        const result = set(state, `[${NAMESPACE}].sourceTaskIdsToShow[${documentId}]`, difference(prevToShow, sourceTaskIds));
        return set(result, `[${NAMESPACE}].sourceTaskIdsToHide[${documentId}]`, difference(prevToHide, sourceTaskIds));
    },
    [actionTypes.GET_GRAPH_GROUPS_NODES_COMPLETED]: (state, { meta }) => {
        if (!reduxStoreExternal.groupsLoaded[meta.documentId])
            reduxStoreExternal.groupsLoaded[meta.documentId] = meta.groupIdArr;
        else
            reduxStoreExternal.groupsLoaded[meta.documentId] = reduxStoreExternal.groupsLoaded[meta.documentId].concat(meta.groupIdArr);
        return state;
    },
    [actionTypes.SET_HAS_CHANGES]: (state, { documentId, hasChanges }) => set(state, `[${NAMESPACE}].hasChanges[${documentId}]`, hasChanges),
    [actionTypes.SET_LAYOUT_ALGORITHM]: (state, { documentId, layoutAlgorithm }) => set(state, `[${NAMESPACE}].layoutAlgorithm[${documentId}]`, layoutAlgorithm),
    [actionTypes.SET_GROUP_TABLE_DATA_FOR_TABLE_VIEW]: (state, { data, documentId }) => set(state, `[${NAMESPACE}].groupTableData[${documentId}]`, data),
    [actionTypes.SET_GROUP_TABLE_MODAL_STATE]: (state, { isOpen }) => set(state, `[${NAMESPACE}].tableModalState`, isOpen),
    [actionTypes.APPEND_TO_UPDATES]: (state, { documentId, nodeSource, edgeSource }) => setUpdatesForDocument(state, { NodesSource: [nodeSource], EdgesSource: edgeSource ? [edgeSource] : undefined }, documentId, true)
});
listenerMiddleware.addListener({
    [signalRActionTypes.REQUEST_TASK_COMPLETED.redux]: ({ message }, dispatch, getState) => {
        if (message.documentId && getActiveDocumentId(getState()) === message.documentId)
            dispatch(actionCreators.getDocumentGraphDataUpdates(message.documentId));
    },
    [documentsActionTypes.DELETE_DOCUMENT_COMPLETED]: ({ meta }, dispatch) => {
        dispatch(actionCreators.clearData(meta.documentId));
    },
    [documentsSidebarMenuActionTypes.SET_DOCUMENT_SELECTED]: ({ documentId, selected }, dispatch) => {
        if (documentId && !selected)
            dispatch(actionCreators.clearData(documentId));
    }
});
